import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginPage, ProtectedRoute } from '@/features/auth';
import { AccountPage } from '@/pages/AccountPage';
import { ClubPlayersPage } from '@/pages/ClubPlayersPage';
import { LiveMatchesPage } from '@/pages/LiveMatchesPage';
import { CreateTournamentPage, EditTournamentPage } from '@/pages/CreateTournamentPage';
import { InfrastructurePage } from '@/pages/InfrastructurePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { MatchResultPage } from '@/pages/MatchResultPage';
import { PublicTournamentScreenPage } from '@/pages/PublicTournamentScreenPage';
import { ScheduledMatchesPage } from '@/pages/ScheduledMatchesPage';
import { SectionPlaceholderPage } from '@/pages/SectionPlaceholderPage';
import { TournamentDrawPage } from '@/pages/TournamentDrawPage';
import { TournamentInfoPage } from '@/pages/TournamentInfoPage';
import { TournamentMatchesPage } from '@/pages/TournamentMatchesPage';
import { TournamentParticipantsPage } from '@/pages/TournamentParticipantsPage';
import { TournamentProgressPage } from '@/pages/TournamentProgressPage';
import { TournamentsPage } from '@/pages/TournamentsPage';
import { ClubsAdminPage } from '@/pages/ClubsAdminPage';
import { UsersAdminPage } from '@/pages/UsersAdminPage';
import { PUBLIC_SCREEN_CLUBS } from '@/shared/config/publicScreens';
import { Spinner } from '@/shared/ui';
import { AppLayout } from './layout/AppLayout';
import { TournamentLayout } from './layout/TournamentLayout';

// Przeglad komponentow sluzy wylacznie do akceptacji stylu i ciagnie za soba
// dane przykladowe, wiec ladujemy go dopiero przy wejsciu na `/ui`.
const UiKitPage = lazy(() =>
  import('@/pages/UiKitPage').then((module) => ({ default: module.UiKitPage })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

/** Zaslepki sekcji bez wlasnego widoku — tytul bierzemy z tej samej listy co nawigacja. */
function MatchesPage() {
  const { t } = useTranslation('common');
  return <SectionPlaceholderPage title={t('nav.matches')} />;
}

function PlayersPage() {
  const { t } = useTranslation('common');
  return <SectionPlaceholderPage title={t('nav.players')} />;
}

function RankingsPage() {
  const { t } = useTranslation('common');
  return <SectionPlaceholderPage title={t('nav.rankings')} />;
}

function SettingsPage() {
  const { t } = useTranslation('common');
  return <SectionPlaceholderPage title={t('nav.settings')} />;
}

/**
 * Mapa tras aplikacji.
 *
 * Adresy sa po polsku, bo aplikacja jest polskojezyczna. Przy dokladaniu
 * kolejnych jezykow zamienimy je na segmenty tlumaczone.
 */
export function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/logowanie" element={<LoginPage />} />

        {/* Przeglad biblioteki komponentow — pomocniczy, poza obszarem chronionym. */}
        <Route path="/ui" element={<UiKitPage />} />

        {/* Tryb prezentacji — publiczny ekran turniejow klubu, bez logowania.
            Skroty klubow zyja na najwyzszym poziomie (`/akademia-sportu`) i nie
            koliduja z trasami chronionymi — segmenty stale wygrywaja z dynamicznymi. */}
        {Object.keys(PUBLIC_SCREEN_CLUBS).map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={<PublicTournamentScreenPage slug={slug} />}
          />
        ))}
        {/* Stary adres ekranu prezentacji — linki i kody QR sprzed zmiany. */}
        <Route path="/turnieje/as" element={<Navigate to="/akademia-sportu" replace />} />

        <Route element={<ProtectedRoute />}>
          {/* Wszystkie widoki po zalogowaniu dziela pasek boczny i pasek gorny. */}
          <Route element={<AppLayout />}>
            {/* Dashboardy sa wylaczone z ruchu — aplikacja zaczyna sie i konczy
                na module turniejow. Trasy zostaja jako przekierowania, zeby
                stare zakladki i linki nie konczyly sie na 404. */}
            <Route path="/" element={<Navigate to="/turnieje" replace />} />
            <Route path="/konto" element={<AccountPage />} />
            <Route path="/turnieje" element={<TournamentsPage />} />
            <Route path="/turnieje/dashboard" element={<Navigate to="/turnieje" replace />} />
            <Route path="/turnieje/nowy" element={<CreateTournamentPage />} />
            {/* Pozycje menu obszaru turniejow. Segmenty stale wygrywaja
                z `:competitionId` — router szereguje trasy po dokladnosci. */}
            <Route path="/turnieje/zawodnicy" element={<ClubPlayersPage />} />
            <Route path="/turnieje/rozpoczete" element={<LiveMatchesPage />} />
            <Route path="/turnieje/zaplanowane" element={<ScheduledMatchesPage />} />
            <Route path="/turnieje/infrastruktura" element={<InfrastructurePage />} />
            {/* Stary adres przydziału kortów — wszystko żyje teraz na jednym widoku. */}
            <Route
              path="/turnieje/infrastruktura/przydzial-kortow"
              element={<Navigate to="/turnieje/infrastruktura" replace />}
            />
            <Route path="/turnieje/:competitionId/edycja" element={<EditTournamentPage />} />
            {/* Wynik meczu żyje poza zakładkami — pełnoekranowy formularz. */}
            <Route
              path="/turnieje/:competitionId/mecze/:matchId/wynik"
              element={<MatchResultPage />}
            />

            {/* Centrum turnieju — wspólny nagłówek i zakładki etapów. */}
            <Route path="/turnieje/:competitionId" element={<TournamentLayout />}>
              <Route index element={<Navigate to="informacje" replace />} />
              <Route path="informacje" element={<TournamentInfoPage />} />
              <Route path="zawodnicy" element={<TournamentParticipantsPage />} />
              <Route path="losowanie" element={<TournamentDrawPage />} />
              <Route path="drabinka" element={<TournamentProgressPage />} />
              <Route path="faza-finalowa" element={<TournamentProgressPage finalPhase />} />
              <Route path="mecze" element={<TournamentMatchesPage />} />
            </Route>
            <Route path="/mecze" element={<MatchesPage />} />
            <Route path="/gracze" element={<PlayersPage />} />
            <Route path="/rankingi" element={<RankingsPage />} />
            <Route path="/ustawienia" element={<SettingsPage />} />
            {/* Panele administratora — strony same pokazują 403 bez roli ADMIN. */}
            <Route path="/uzytkownicy" element={<UsersAdminPage />} />
            <Route path="/kluby" element={<ClubsAdminPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
